import { router } from '@inertiajs/react'
import { Badge } from '@/components/ui/badge'
import { TableCell, TableRow } from '@/components/ui/table'
import { InlineStatusEdit } from './InlineStatusEdit'
import {
  TASK_PRIORITY_LABELS,
  TASK_TYPE_LABELS,
  type TaskRow as TaskRowType,
} from '@/types/work'

const PRIORITY_BADGE_VARIANT: Record<string, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  low: 'outline',
  medium: 'secondary',
  high: 'default',
  urgent: 'destructive',
}

interface TaskRowProps {
  task: TaskRowType
  // Inline edits aren't gated per-row in v1 — the server returns 403 if
  // the actor lacks permission and the optimistic update rolls back.
  // When PR 8's `policy_caps` is plumbed onto every row in a future
  // pass, gate via `task.policy_caps?.update_status` instead.
  canEditStatus: boolean
  // Click handler for opening the task in TaskDetailSheet on the My
  // Tasks index. The anchor `href` is preserved so middle-click /
  // cmd-click / ctrl-click still opens the standalone page in a new
  // tab via the browser's native handling — only primary left-click
  // is intercepted.
  onTaskClick: (id: number) => void
  // Inertia partial-reload prop keys to refresh after an inline status
  // edit. Defaults to the flat-list set; the grouped table passes
  // `team_groups` too so a status change that empties/repopulates a team
  // section refreshes its header and count instead of going stale.
  reloadProps?: string[]
}

export function TaskRow({
  task,
  canEditStatus,
  onTaskClick,
  reloadProps = ['tasks', 'pagy', 'filters'],
}: TaskRowProps) {
  function handleTitleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    // Let modified clicks (open-in-new-tab, etc.) fall through to the
    // browser's default anchor handling.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    onTaskClick(task.id)
  }

  return (
    <TableRow>
      <TableCell className="font-medium">
        <a
          href={`/work/tasks/${task.id}`}
          onClick={handleTitleClick}
          className="hover:underline"
        >
          {task.title}
        </a>
        {task.subtask_count > 0 && (
          <span className="ml-2 text-xs text-muted-foreground">
            {task.subtask_count} sous-tâche{task.subtask_count > 1 ? 's' : ''}
          </span>
        )}
      </TableCell>
      <TableCell>
        <InlineStatusEdit
          taskId={task.id}
          value={task.status}
          canUpdate={canEditStatus}
          onAfterChange={() => router.reload({ only: reloadProps })}
        />
      </TableCell>
      <TableCell>
        <Badge variant={PRIORITY_BADGE_VARIANT[task.priority] ?? 'outline'}>
          {TASK_PRIORITY_LABELS[task.priority]}
        </Badge>
      </TableCell>
      <TableCell className="text-sm">{TASK_TYPE_LABELS[task.task_type]}</TableCell>
      <TableCell className="text-sm">
        {task.due_at ? new Date(task.due_at).toLocaleDateString('fr-FR') : '—'}
      </TableCell>
      <TableCell className="text-sm">
        {task.assignee_name ?? <span className="text-muted-foreground">Non assigné</span>}
      </TableCell>
      <TableCell className="text-sm">
        {task.team_name ?? <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-sm">
        {task.created_by_name ?? <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-sm">
        {task.subject ? (
          <span>
            {task.subject.label}
            {task.subject.sublabel && (
              <span className="ml-1 text-xs text-muted-foreground">{task.subject.sublabel}</span>
            )}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  )
}
